// src/components/blog/BlogGrid.jsx

import React, { memo } from 'react';
import BlogCard from './BlogCard';

/**
 * Responsive blog grid component
 * @param {Object} props
 * @param {Array} props.blogs - Array of blog posts
 * @param {string} props.variant - 'featured' or 'grid'
 * @param {boolean} props.isDark - Dark mode flag
 * @param {Function} props.onImageError - Image error handler
 * @param {string} props.emptyMessage - Message when no blogs
 */
const BlogGrid = memo(({ 
  blogs = [], 
  variant = 'grid',
  isDark = false, 
  onImageError,
  emptyMessage = 'No posts found'
}) => {
  if (!blogs.length) {
    return (
      <div className={`text-center py-12 rounded-xl ${
        isDark 
          ? 'bg-white/5 border border-white/10' 
          : 'bg-white border border-gray-200'
      }`}>
        <div className="text-6xl mb-4">🔍</div>
        <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {emptyMessage}
        </h3>
        <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
          Try adjusting your search or filter to find what you're looking for.
        </p>
      </div>
    );
  }

  if (variant === 'featured') {
    // Only show first blog as featured
    const featuredBlog = blogs[0];
    return (
      <div className="mb-12">
        <BlogCard 
          blog={featuredBlog} 
          variant="featured" 
          isDark={isDark}
          onImageError={onImageError}
        />
      </div>
    );
  }

  // Grid variant
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {blogs.map((blog) => (
        <BlogCard 
          key={blog.id} 
          blog={blog} 
          variant="default"
          isDark={isDark}
          onImageError={onImageError}
        />
      ))}
    </div>
  );
});

BlogGrid.displayName = 'BlogGrid';

export default BlogGrid;